// CC3.MVP W3b · Portal shell + client-scoped reads.
//
// Isolation guarantees:
//   * Uses ONLY `aa_portal_token` — never reads or writes the internal
//     `aa_ops_token` used by the AAC ops app.
//   * Renders on a dedicated route tree (`/portal/clients/*`), separate
//     from the internal shell. No internal nav item leaks in.
//
// Endpoints (all W2-canonical, prefixed with `/api`):
//   GET /portal/clients
//   GET /portal/clients/:cid/tours
//   GET /portal/clients/:cid/tours/:tid
//   GET /portal/clients/:cid/quotes
//   GET /portal/clients/:cid/quotes/:qid
//
// Live CCR is always the authoritative access boundary. The Contact's
// `last_selected_customer_id` and the URL `:cid` are UX hints; the
// server re-checks CCR on every read.

import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import axios from "axios";
import {
  Link,
  Navigate,
  Route,
  Routes,
  useLocation,
  useNavigate,
  useParams,
} from "react-router-dom";
import { Loader2, ChevronRight, LogOut, Menu, X, Users, Route as RouteIcon, FileText } from "lucide-react";
import { tourDisplayLabel } from "@/lib/tourDisplayLabel";

const AA_PORTAL_TOKEN = "aa_portal_token";
const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// -------- Isolated axios instance ------------------------------------
const portalHttp = axios.create({ baseURL: API });
portalHttp.interceptors.request.use((cfg) => {
  const t = localStorage.getItem(AA_PORTAL_TOKEN);
  if (t) cfg.headers.Authorization = `Bearer ${t}`;
  return cfg;
});
portalHttp.interceptors.response.use(
  (r) => r,
  (err) => {
    if (err?.response?.status === 401) {
      localStorage.removeItem(AA_PORTAL_TOKEN);
      window.location.replace("/portal/login");
    }
    return Promise.reject(err);
  }
);

// -------- Session/Client context -------------------------------------
const W3bCtx = createContext(null);

function W3bProvider({ children }) {
  const [me, setMe] = useState(null);
  const [clients, setClients] = useState([]);
  const [defaultClientId, setDefaultClientId] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const meRes = await portalHttp.get("/portal/session/me");
      setMe(meRes.data);
      const cRes = await portalHttp.get("/portal/clients");
      setClients(cRes.data?.clients || []);
      setDefaultClientId(cRes.data?.default_client_id || null);
    } catch (e) {
      const code = e?.response?.data?.detail?.code;
      if (code === "no_authorized_clients") {
        setClients([]);
        setDefaultClientId(null);
        setMe((prev) => prev || { access: "no_clients" });
      } else if (e?.response?.status === 401 || e?.response?.status === 403) {
        setError("session_expired");
      } else {
        setError("load_failed");
      }
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const signOut = useCallback(() => {
    localStorage.removeItem(AA_PORTAL_TOKEN);
    window.location.replace("/portal/login");
  }, []);

  const value = useMemo(
    () => ({ me, clients, defaultClientId, loading, error, reload: load, signOut }),
    [me, clients, defaultClientId, loading, error, load, signOut]
  );

  return <W3bCtx.Provider value={value}>{children}</W3bCtx.Provider>;
}

function useW3b() {
  const v = useContext(W3bCtx);
  if (!v) throw new Error("useW3b must be used within W3bProvider");
  return v;
}

// -------- Presentation primitives ------------------------------------
const Wordmark = () => (
  <div className="flex flex-col leading-tight" data-testid="portal-wordmark">
    <span
      className="text-white font-semibold tracking-[0.14em] text-[15px]"
      style={{ fontFamily: "Montserrat, sans-serif" }}
    >
      ALL ACCESS PORTAL
    </span>
    <span
      data-testid="portal-wordmark-subtitle"
      className="text-white text-[10.5px]"
    >
      Client Portal · authorized personnel only
    </span>
  </div>
);

// Pre-W4 Owner polish · customer-facing Tour lifecycle. Internal
// canonical TourStatus values are the source of truth; this module
// only projects them onto the six customer-facing stages the Portal
// exposes. Nothing here mutates records.
const PORTAL_STAGES = [
  { key: "inquiry", label: "Inquiry" },
  { key: "routing", label: "Routing" },
  { key: "quote", label: "Quote" },
  { key: "contract", label: "Contract" },
  { key: "booked", label: "Booked" },
  { key: "departure", label: "Departure" },
];

const INTERNAL_STATUS_TO_PORTAL_STAGE = {
  inquiry: "inquiry",
  availability_review: "routing",
  soft_hold: "quote",
  first_hold: "quote",
  second_hold: "quote",
  contract_pending: "contract",
  ready_to_book: "contract",
  booked: "booked",
  dispatch_ready: "departure",
  dispatched: "departure",
  active_tour: "departure",
  completed: "departure",
  billing: "departure",
  closed: "departure",
  // `cancelled` is intentionally omitted — rendered as its own state.
};

function portalStageOf(internalStatus) {
  if (!internalStatus) return null;
  if (internalStatus === "cancelled") return "cancelled";
  return INTERNAL_STATUS_TO_PORTAL_STAGE[internalStatus] || null;
}

function PortalLifecycleTracker({ internalStatus }) {
  const stage = portalStageOf(internalStatus);
  const isCancelled = stage === "cancelled";
  const activeIndex = isCancelled
    ? -1
    : PORTAL_STAGES.findIndex((s) => s.key === stage);

  return (
    <div data-testid="portal-lifecycle" className="mt-1">
      <div className="text-[10.5px] uppercase tracking-widest text-[#6B747C] mb-2">
        Tour lifecycle
      </div>
      {isCancelled ? (
        <div
          data-testid="portal-lifecycle-cancelled"
          className="inline-flex items-center gap-2 border border-[#B83C3C] bg-[#B83C3C]/10 text-[#B83C3C] rounded-sm px-3 py-1.5 text-[12px] uppercase tracking-widest"
        >
          Cancelled
        </div>
      ) : (
        <ol
          className="flex items-center flex-wrap gap-y-2"
          data-testid="portal-lifecycle-track"
        >
          {PORTAL_STAGES.map((s, i) => {
            const isDone = activeIndex >= 0 && i < activeIndex;
            const isCurrent = activeIndex >= 0 && i === activeIndex;
            return (
              <li
                key={s.key}
                data-testid={`portal-lifecycle-stage-${s.key}`}
                data-stage-state={isCurrent ? "current" : isDone ? "done" : "upcoming"}
                className="flex items-center"
              >
                <div
                  className={`flex items-center gap-1.5 px-2.5 py-1 rounded-sm border text-[11.5px] ${
                    isCurrent
                      ? "border-[#1B4F8A] bg-[#1B4F8A] text-white"
                      : isDone
                      ? "border-[#1B4F8A] bg-[#1B4F8A]/10 text-[#1B4F8A]"
                      : "border-[#E8EBED] bg-white text-[#6B747C]"
                  }`}
                >
                  <span
                    className={`w-4 h-4 rounded-full grid place-items-center text-[9px] font-semibold ${
                      isCurrent
                        ? "bg-white text-[#1B4F8A]"
                        : isDone
                        ? "bg-[#1B4F8A] text-white"
                        : "bg-[#E8EBED] text-[#6B747C]"
                    }`}
                  >
                    {i + 1}
                  </span>
                  {s.label}
                </div>
                {i < PORTAL_STAGES.length - 1 && (
                  <span
                    className={`mx-1.5 h-px w-4 md:w-6 ${
                      isDone ? "bg-[#1B4F8A]" : "bg-[#E8EBED]"
                    }`}
                    aria-hidden="true"
                  />
                )}
              </li>
            );
          })}
        </ol>
      )}
    </div>
  );
}

function ClientSwitcher({ activeClientId, onSwitch }) {
  const { clients } = useW3b();
  const [open, setOpen] = useState(false);
  const active = clients.find((c) => c.id === activeClientId);
  if (!clients || clients.length === 0) return null;
  if (clients.length === 1) {
    return (
      <span
        data-testid="portal-client-single"
        className="text-[12.5px] text-[#C7CDD2] px-2 py-1 rounded-sm border border-[#22272B]"
      >
        {active?.display_name || clients[0].display_name}
      </span>
    );
  }
  return (
    <div className="relative">
      <button
        data-testid="portal-client-switcher"
        onClick={() => setOpen((v) => !v)}
        className="inline-flex items-center gap-2 px-3 py-1.5 text-[12.5px] text-white bg-[#22272B] hover:bg-[#2c3236] border border-[#22272B] rounded-sm"
      >
        <Users size={13} />
        <span className="max-w-[220px] truncate">
          {active?.display_name || "Select client"}
        </span>
        <ChevronRight size={12} className={open ? "rotate-90 transition" : "transition"} />
      </button>
      {open && (
        <ul
          data-testid="portal-client-switcher-menu"
          className="absolute right-0 mt-1 min-w-[260px] max-h-64 overflow-auto bg-white border border-[#C7CDD2] rounded-sm shadow-lg z-50"
          onMouseLeave={() => setOpen(false)}
        >
          {clients.map((c) => (
            <li
              key={c.id}
              data-testid={`portal-client-switcher-item-${c.id}`}
              onClick={() => {
                setOpen(false);
                onSwitch(c.id);
              }}
              className={`px-3 py-2 text-[12.5px] cursor-pointer flex items-center justify-between gap-3 ${
                c.id === activeClientId ? "bg-[#F7F8F9]" : "hover:bg-[#F7F8F9]"
              }`}
            >
              <span className="truncate text-[#111315]">{c.display_name}</span>
              <span className="text-[10px] uppercase tracking-widest text-[#6B747C]">
                {c.customer_number}
              </span>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

function PortalTopNav({ activeClientId, onSwitch }) {
  const { me, signOut } = useW3b();
  return (
    <header
      data-testid="portal-top-nav"
      className="hidden md:flex items-center justify-between bg-[#111315] px-6 py-3 border-b border-[#22272B]"
    >
      <Wordmark />
      <div className="flex items-center gap-3">
        <ClientSwitcher activeClientId={activeClientId} onSwitch={onSwitch} />
        <span className="text-[11.5px] text-[#C7CDD2] max-w-[200px] truncate" data-testid="portal-me-email">
          {me?.email}
        </span>
        <button
          data-testid="portal-signout"
          onClick={signOut}
          className="inline-flex items-center gap-1.5 text-[12px] text-[#C7CDD2] hover:text-white border border-[#22272B] px-2 py-1 rounded-sm"
        >
          <LogOut size={12} /> Sign out
        </button>
      </div>
    </header>
  );
}

function MobileTopBar({ onOpenDrawer, activeClientId, onSwitch }) {
  return (
    <header
      data-testid="portal-mobile-top-bar"
      className="md:hidden flex items-center justify-between bg-[#111315] px-3 py-2 border-b border-[#22272B]"
    >
      <button
        data-testid="portal-mobile-drawer-open"
        onClick={onOpenDrawer}
        className="p-2 text-white hover:bg-[#22272B] rounded-sm"
        aria-label="Open navigation"
      >
        <Menu size={18} />
      </button>
      <Wordmark />
      <ClientSwitcher activeClientId={activeClientId} onSwitch={onSwitch} />
    </header>
  );
}

function MobileDrawer({ open, onClose, activeClientId }) {
  const { clients, signOut } = useW3b();
  const nav = useNavigate();
  if (!open) return null;
  const go = (path) => {
    nav(path);
    onClose();
  };
  return (
    <div
      data-testid="portal-mobile-drawer"
      className="md:hidden fixed inset-0 z-50 flex"
      role="dialog"
      aria-modal="true"
    >
      <div className="absolute inset-0 bg-black/50" onClick={onClose} />
      <aside className="relative bg-white w-72 max-w-[85vw] h-full shadow-xl flex flex-col">
        <div className="bg-[#111315] px-4 py-3 flex items-center justify-between">
          <Wordmark />
          <button
            data-testid="portal-mobile-drawer-close"
            onClick={onClose}
            className="p-1.5 text-white hover:bg-[#22272B] rounded-sm"
            aria-label="Close navigation"
          >
            <X size={16} />
          </button>
        </div>
        <div className="p-3 space-y-1 overflow-y-auto flex-1">
          <div className="text-[10.5px] uppercase tracking-widest text-[#6B747C] mt-1 mb-1 px-1">
            Clients
          </div>
          {clients.map((c) => (
            <button
              key={c.id}
              data-testid={`portal-mobile-nav-client-${c.id}`}
              onClick={() => go(`/portal/clients/${c.id}/tours`)}
              className={`w-full text-left px-3 py-2 text-[13px] rounded-sm border ${
                c.id === activeClientId
                  ? "border-[#1B4F8A] bg-[#1B4F8A]/5 text-[#111315]"
                  : "border-transparent hover:bg-[#F7F8F9] text-[#111315]"
              }`}
            >
              <div className="truncate font-medium">{c.display_name}</div>
              <div className="text-[10px] uppercase tracking-widest text-[#6B747C]">
                {c.customer_number}
              </div>
            </button>
          ))}
          {clients.length === 0 && (
            <div className="text-[12px] text-[#6B747C] px-1 py-2">No authorized clients.</div>
          )}
          {activeClientId && (
            <>
              <div className="text-[10.5px] uppercase tracking-widest text-[#6B747C] mt-4 mb-1 px-1">
                {clients.find((c) => c.id === activeClientId)?.display_name}
              </div>
              <button
                data-testid="portal-mobile-nav-tours"
                onClick={() => go(`/portal/clients/${activeClientId}/tours`)}
                className="w-full text-left px-3 py-2 text-[13px] rounded-sm hover:bg-[#F7F8F9] text-[#111315] flex items-center gap-2"
              >
                <RouteIcon size={14} /> Tours
              </button>
              <button
                data-testid="portal-mobile-nav-quotes"
                onClick={() => go(`/portal/clients/${activeClientId}/quotes`)}
                className="w-full text-left px-3 py-2 text-[13px] rounded-sm hover:bg-[#F7F8F9] text-[#111315] flex items-center gap-2"
              >
                <FileText size={14} /> Quotes
              </button>
            </>
          )}
        </div>
        <div className="border-t border-[#E8EBED] p-3">
          <button
            data-testid="portal-mobile-signout"
            onClick={signOut}
            className="w-full text-left px-3 py-2 text-[13px] rounded-sm hover:bg-[#F7F8F9] text-[#111315] flex items-center gap-2"
          >
            <LogOut size={14} /> Sign out
          </button>
        </div>
      </aside>
    </div>
  );
}

function MobileMoreSheet({ open, onClose }) {
  const { me, signOut } = useW3b();
  if (!open) return null;
  return (
    <div
      data-testid="portal-more-sheet"
      className="md:hidden fixed inset-0 z-50"
      role="dialog"
      aria-modal="true"
    >
      <div className="absolute inset-0 bg-black/50" onClick={onClose} />
      <div className="absolute inset-x-0 bottom-0 bg-white rounded-t-lg border-t border-[#E8EBED] shadow-xl p-4 space-y-1">
        <div className="flex items-center justify-between pb-2 border-b border-[#E8EBED]">
          <div>
            <div className="text-[11px] uppercase tracking-widest text-[#6B747C]">Signed in as</div>
            <div className="text-[13px] text-[#111315] truncate max-w-[240px]" data-testid="portal-more-me">
              {me?.email || "—"}
            </div>
          </div>
          <button
            data-testid="portal-more-close"
            onClick={onClose}
            className="p-1.5 text-[#6B747C] hover:text-[#111315]"
            aria-label="Close"
          >
            <X size={16} />
          </button>
        </div>
        <button
          data-testid="portal-more-signout"
          onClick={signOut}
          className="w-full text-left px-3 py-2.5 text-[13px] hover:bg-[#F7F8F9] rounded-sm flex items-center gap-2"
        >
          <LogOut size={14} /> Sign out
        </button>
      </div>
    </div>
  );
}

function MobileBottomBar({ onOpenMore, activeClientId }) {
  const location = useLocation();
  const tabs = activeClientId
    ? [
        { key: "tours", label: "Tours", icon: RouteIcon, path: `/portal/clients/${activeClientId}/tours` },
        { key: "quotes", label: "Quotes", icon: FileText, path: `/portal/clients/${activeClientId}/quotes` },
      ]
    : [];
  return (
    <nav
      data-testid="portal-mobile-bottom-bar"
      className="md:hidden fixed bottom-0 inset-x-0 bg-white border-t border-[#E8EBED] flex justify-around z-30"
      style={{ paddingBottom: "env(safe-area-inset-bottom, 0px)" }}
    >
      {tabs.map((t) => {
        const active = location.pathname.startsWith(t.path);
        return (
          <Link
            key={t.key}
            to={t.path}
            data-testid={`portal-bottom-${t.key}`}
            className={`flex-1 py-2 text-center text-[11.5px] ${
              active ? "text-[#1B4F8A] font-semibold" : "text-[#6B747C]"
            }`}
          >
            <t.icon size={16} className="mx-auto mb-0.5" />
            {t.label}
          </Link>
        );
      })}
      <button
        onClick={onOpenMore}
        data-testid="portal-bottom-more"
        className="flex-1 py-2 text-center text-[11.5px] text-[#6B747C]"
      >
        <Menu size={16} className="mx-auto mb-0.5" />
        More
      </button>
    </nav>
  );
}

// -------- Content components ----------------------------------------
function CenteredLoader({ testid = "portal-loading" }) {
  return (
    <div data-testid={testid} className="grid place-items-center py-16">
      <Loader2 size={22} className="animate-spin text-[#6B747C]" />
    </div>
  );
}

function EmptyPanel({ testid, title, note }) {
  return (
    <div
      data-testid={testid}
      className="bg-white border border-[#E8EBED] rounded-sm p-6 text-center"
    >
      <div className="text-[13.5px] font-medium text-[#111315]">{title}</div>
      {note && <div className="text-[12px] text-[#6B747C] mt-1">{note}</div>}
    </div>
  );
}

function NoAuthorizedClientsView() {
  const { signOut, me } = useW3b();
  return (
    <div
      data-testid="portal-no-authorized-clients"
      className="max-w-md mx-auto bg-white border border-[#E8EBED] rounded-sm p-6 mt-16"
    >
      <div className="text-[15px] font-semibold text-[#111315]">
        No clients are currently linked to your account
      </div>
      <div className="text-[13px] text-[#6B747C] mt-2">
        Your account manager can grant you access to one or more clients.
      </div>
      {me?.email && (
        <div className="text-[12px] text-[#6B747C] mt-3">
          Signed in as <span className="text-[#111315]">{me.email}</span>.
        </div>
      )}
      <button
        data-testid="portal-no-clients-signout"
        onClick={signOut}
        className="mt-4 w-full text-[13px] py-2 border border-[#C7CDD2] rounded-sm hover:bg-[#F7F8F9]"
      >
        Sign out
      </button>
    </div>
  );
}

function ClientPicker() {
  const { clients, loading, defaultClientId } = useW3b();
  const nav = useNavigate();

  useEffect(() => {
    if (loading) return;
    if (clients.length === 1) {
      // Single-client shortcut per W3b spec.
      nav(`/portal/clients/${clients[0].id}/tours`, { replace: true });
      return;
    }
  }, [clients, loading, nav]);

  if (loading) return <CenteredLoader />;
  if (clients.length === 0) return <NoAuthorizedClientsView />;

  return (
    <section
      data-testid="portal-client-picker"
      className="max-w-2xl mx-auto py-4"
    >
      <h1
        className="text-[20px] md:text-[22px] font-semibold text-[#111315]"
        style={{ fontFamily: "Montserrat, sans-serif" }}
      >
        Choose a client
      </h1>
      <p className="text-[13px] text-[#6B747C] mt-1">
        You are authorized on {clients.length} clients. Each client's tours,
        quotes, and documents are scoped separately.
      </p>
      <ul className="mt-4 space-y-2">
        {clients.map((c) => (
          <li key={c.id}>
            <Link
              to={`/portal/clients/${c.id}/tours`}
              data-testid={`portal-picker-item-${c.id}`}
              className={`flex items-center justify-between bg-white border rounded-sm px-4 py-3 hover:border-[#1B4F8A] ${
                defaultClientId === c.id ? "border-[#1B4F8A]" : "border-[#E8EBED]"
              }`}
            >
              <div>
                <div className="text-[14px] font-medium text-[#111315] truncate">
                  {c.display_name}
                </div>
                <div className="text-[11px] uppercase tracking-widest text-[#6B747C]">
                  {c.customer_number}
                </div>
              </div>
              <ChevronRight size={16} className="text-[#6B747C]" />
            </Link>
          </li>
        ))}
      </ul>
    </section>
  );
}

function ClientHeader({ client, activeSection }) {
  return (
    <div
      className="mb-4 flex flex-wrap items-end justify-between gap-2 border-b border-[#E8EBED] pb-3"
      data-testid="portal-client-header"
    >
      <div>
        <div className="text-[10.5px] uppercase tracking-widest text-[#6B747C]">
          Client
        </div>
        <div
          className="text-[18px] md:text-[20px] font-semibold text-[#111315]"
          style={{ fontFamily: "Montserrat, sans-serif" }}
        >
          {client?.display_name || "—"}
        </div>
        <div className="text-[11px] uppercase tracking-widest text-[#6B747C]">
          {client?.customer_number}
        </div>
      </div>
      <nav
        data-testid="portal-client-section-tabs"
        className="hidden sm:flex gap-1 text-[12.5px]"
      >
        <Link
          to={`/portal/clients/${client?.id}/tours`}
          data-testid="portal-tab-tours"
          className={`px-3 py-1.5 rounded-sm border ${
            activeSection === "tours"
              ? "bg-[#1B4F8A] text-white border-[#1B4F8A]"
              : "text-[#111315] border-[#E8EBED] hover:bg-[#F7F8F9]"
          }`}
        >
          Tours
        </Link>
        <Link
          to={`/portal/clients/${client?.id}/quotes`}
          data-testid="portal-tab-quotes"
          className={`px-3 py-1.5 rounded-sm border ${
            activeSection === "quotes"
              ? "bg-[#1B4F8A] text-white border-[#1B4F8A]"
              : "text-[#111315] border-[#E8EBED] hover:bg-[#F7F8F9]"
          }`}
        >
          Quotes
        </Link>
      </nav>
    </div>
  );
}

function useClientById(cid) {
  const { clients, loading } = useW3b();
  const client = clients.find((c) => c.id === cid) || null;
  const authorized = !loading && !!client;
  return { client, authorized, loading };
}

function TourList() {
  const { cid } = useParams();
  const { client, authorized, loading: sessLoading } = useClientById(cid);
  const [rows, setRows] = useState(null);
  const [status, setStatus] = useState("loading");

  useEffect(() => {
    if (sessLoading) return;
    if (!authorized) {
      setStatus("unauthorized");
      return;
    }
    setStatus("loading");
    setRows(null);
    portalHttp
      .get(`/portal/clients/${cid}/tours`)
      .then((r) => {
        setRows(r.data?.tours || []);
        setStatus("ok");
      })
      .catch((e) => {
        setStatus(e?.response?.status === 404 ? "unauthorized" : "error");
      });
  }, [cid, authorized, sessLoading]);

  if (sessLoading || status === "loading") return <CenteredLoader testid="portal-tours-loading" />;
  if (status === "unauthorized") return <UnauthorizedClientPanel />;

  return (
    <div data-testid="portal-tour-list-page">
      <ClientHeader client={client} activeSection="tours" />
      {rows && rows.length === 0 ? (
        <EmptyPanel
          testid="portal-tours-empty"
          title="No tours yet"
          note="Tours will appear here once your account manager has published them."
        />
      ) : (
        <ul className="space-y-2" data-testid="portal-tour-list">
          {(rows || []).map((t) => (
            <li key={t.id}>
              <Link
                to={`/portal/clients/${cid}/tours/${t.id}`}
                data-testid={`portal-tour-row-${t.id}`}
                className="flex items-center justify-between bg-white border border-[#E8EBED] rounded-sm px-4 py-3 hover:border-[#1B4F8A]"
              >
                <div className="min-w-0">
                  <div className="text-[13.5px] font-medium text-[#111315] truncate">
                    {tourDisplayLabel(t, { clientName: client?.display_name })}
                  </div>
                  <div className="text-[11px] uppercase tracking-widest text-[#6B747C]">
                    {/* RC4 · Portal ITEM 7 — tour number hidden. Show
                        only status; internal id remains in the URL for
                        routing. */}
                    {t.status || "—"}
                  </div>
                </div>
                <div className="text-[11.5px] text-[#6B747C] shrink-0">
                  {t.start_date || "—"} → {t.end_date || "—"}
                </div>
              </Link>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

function TourDetail() {
  const { cid, tid } = useParams();
  const { client, authorized, loading: sessLoading } = useClientById(cid);
  const [tour, setTour] = useState(null);
  const [status, setStatus] = useState("loading");

  useEffect(() => {
    if (sessLoading) return;
    if (!authorized) {
      setStatus("unauthorized");
      return;
    }
    setStatus("loading");
    portalHttp
      .get(`/portal/clients/${cid}/tours/${tid}`)
      .then((r) => {
        setTour(r.data?.tour || null);
        setStatus(r.data?.tour ? "ok" : "not_found");
      })
      .catch((e) => {
        setStatus(e?.response?.status === 404 ? "not_found" : "error");
      });
  }, [cid, tid, authorized, sessLoading]);

  if (sessLoading || status === "loading") return <CenteredLoader testid="portal-tour-loading" />;
  if (status === "unauthorized") return <UnauthorizedClientPanel />;
  if (status === "not_found")
    return (
      <div data-testid="portal-tour-detail-page">
        <ClientHeader client={client} activeSection="tours" />
        <EmptyPanel
          testid="portal-tour-not-found"
          title="Tour not found"
          note="This tour is not available on this client."
        />
      </div>
    );

  return (
    <div data-testid="portal-tour-detail-page">
      <ClientHeader client={client} activeSection="tours" />
      <div className="bg-white border border-[#E8EBED] rounded-sm p-5">
        <div className="text-[10.5px] uppercase tracking-widest text-[#6B747C]">
          {/* RC4 · Portal ITEM 7 — tour number hidden; show client
              name in this eyebrow slot instead. */}
          {client?.display_name || "Client"}
        </div>
        <h2
          className="text-[18px] font-semibold text-[#111315]"
          style={{ fontFamily: "Montserrat, sans-serif" }}
          data-testid="portal-tour-name"
        >
          {tourDisplayLabel(tour, { clientName: client?.display_name })}
        </h2>
        <div className="mt-2 text-[12px] uppercase tracking-widest text-[#6B747C]">
          Status · {tour.status || "—"}
        </div>
        <PortalLifecycleTracker internalStatus={tour.status} />
        <dl className="mt-4 grid grid-cols-2 gap-3 text-[13px]">
          <MetaCell label="Start" value={tour.start_date} testid="portal-tour-start" />
          <MetaCell label="End" value={tour.end_date} testid="portal-tour-end" />
          <MetaCell label="Tour days" value={tour.tour_days} testid="portal-tour-days" />
          <MetaCell label="Billed days" value={tour.billed_days} testid="portal-tour-billed-days" />
          <MetaCell label="Passengers" value={tour.passengers} testid="portal-tour-passengers" />
          <MetaCell label="Estimated mileage" value={tour.estimated_mileage} testid="portal-tour-mileage" />
          <MetaCell label="Contract type" value={tour.contract_type} testid="portal-tour-contract-type" />
          <MetaCell label="Rate type" value={tour.rate_type} testid="portal-tour-rate-type" />
        </dl>
        <div className="mt-6">
          <Link
            to={`/portal/clients/${cid}/quotes`}
            data-testid="portal-tour-view-quotes"
            className="inline-flex items-center gap-1.5 text-[12.5px] text-[#1B4F8A] hover:underline"
          >
            View quotes for this client <ChevronRight size={12} />
          </Link>
        </div>
      </div>
    </div>
  );
}

function MetaCell({ label, value, testid }) {
  return (
    <div data-testid={testid}>
      <div className="text-[10.5px] uppercase tracking-widest text-[#6B747C]">{label}</div>
      <div className="text-[13px] text-[#111315] mt-0.5">
        {value === null || value === undefined || value === "" ? "—" : String(value)}
      </div>
    </div>
  );
}

function QuoteList() {
  const { cid } = useParams();
  const { client, authorized, loading: sessLoading } = useClientById(cid);
  const [rows, setRows] = useState(null);
  const [status, setStatus] = useState("loading");

  useEffect(() => {
    if (sessLoading) return;
    if (!authorized) {
      setStatus("unauthorized");
      return;
    }
    setStatus("loading");
    setRows(null);
    portalHttp
      .get(`/portal/clients/${cid}/quotes`)
      .then((r) => {
        setRows(r.data?.quotes || []);
        setStatus("ok");
      })
      .catch((e) => {
        setStatus(e?.response?.status === 404 ? "unauthorized" : "error");
      });
  }, [cid, authorized, sessLoading]);

  if (sessLoading || status === "loading") return <CenteredLoader testid="portal-quotes-loading" />;
  if (status === "unauthorized") return <UnauthorizedClientPanel />;

  return (
    <div data-testid="portal-quote-list-page">
      <ClientHeader client={client} activeSection="quotes" />
      {rows && rows.length === 0 ? (
        <EmptyPanel
          testid="portal-quotes-empty"
          title="No quotes yet"
          note="Sent quotes will appear here."
        />
      ) : (
        <ul className="space-y-2" data-testid="portal-quote-list">
          {(rows || []).map((q) => (
            <li key={q.id}>
              <Link
                to={`/portal/clients/${cid}/quotes/${q.id}`}
                data-testid={`portal-quote-row-${q.id}`}
                className="flex items-center justify-between bg-white border border-[#E8EBED] rounded-sm px-4 py-3 hover:border-[#1B4F8A]"
              >
                <div className="min-w-0">
                  <div className="text-[13.5px] font-medium text-[#111315] truncate">
                    Quote v{q.version} · {q.status?.toUpperCase()}
                  </div>
                  <div className="text-[11px] uppercase tracking-widest text-[#6B747C]">
                    Sent {q.sent_at ? new Date(q.sent_at).toLocaleDateString() : "—"}
                  </div>
                </div>
                <div className="text-[13px] text-[#111315] shrink-0">
                  ${q.customer_total || "0.00"}
                </div>
              </Link>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

function QuoteDetail() {
  const { cid, qid } = useParams();
  const { client, authorized, loading: sessLoading } = useClientById(cid);
  const [quote, setQuote] = useState(null);
  const [status, setStatus] = useState("loading");

  useEffect(() => {
    if (sessLoading) return;
    if (!authorized) {
      setStatus("unauthorized");
      return;
    }
    setStatus("loading");
    portalHttp
      .get(`/portal/clients/${cid}/quotes/${qid}`)
      .then((r) => {
        setQuote(r.data?.quote || null);
        setStatus(r.data?.quote ? "ok" : "not_found");
      })
      .catch((e) => {
        setStatus(e?.response?.status === 404 ? "not_found" : "error");
      });
  }, [cid, qid, authorized, sessLoading]);

  if (sessLoading || status === "loading") return <CenteredLoader testid="portal-quote-loading" />;
  if (status === "unauthorized") return <UnauthorizedClientPanel />;
  if (status === "not_found")
    return (
      <div data-testid="portal-quote-detail-page">
        <ClientHeader client={client} activeSection="quotes" />
        <EmptyPanel
          testid="portal-quote-not-found"
          title="Quote not found"
          note="This quote is not available on this client."
        />
      </div>
    );

  return (
    <div data-testid="portal-quote-detail-page">
      <ClientHeader client={client} activeSection="quotes" />
      <div className="bg-white border border-[#E8EBED] rounded-sm p-5">
        <div className="text-[10.5px] uppercase tracking-widest text-[#6B747C]">
          Version {quote.version} · {quote.status?.toUpperCase()}
        </div>
        <h2
          className="text-[18px] font-semibold text-[#111315]"
          style={{ fontFamily: "Montserrat, sans-serif" }}
          data-testid="portal-quote-heading"
        >
          Quote total ${quote.customer_total || "0.00"}
        </h2>
        <div className="text-[12px] text-[#6B747C]">
          Sent {quote.sent_at ? new Date(quote.sent_at).toLocaleString() : "—"}
        </div>
        {quote.customer_facing_notes && (
          <div
            data-testid="portal-quote-notes"
            className="mt-4 text-[13px] text-[#111315] bg-[#F7F8F9] border border-[#E8EBED] rounded-sm p-3"
          >
            {quote.customer_facing_notes}
          </div>
        )}
        <div className="mt-6">
          <div className="text-[10.5px] uppercase tracking-widest text-[#6B747C] mb-1">
            Line items
          </div>
          <div
            className="border border-[#E8EBED] rounded-sm overflow-hidden"
            data-testid="portal-quote-lines"
          >
            <table className="w-full text-[12.5px]">
              <thead className="bg-[#F7F8F9] text-[#6B747C]">
                <tr>
                  <th className="text-left px-3 py-2">Description</th>
                  <th className="text-right px-3 py-2 w-20">Qty</th>
                  <th className="text-right px-3 py-2 w-24">Rate</th>
                  <th className="text-right px-3 py-2 w-28">Amount</th>
                </tr>
              </thead>
              <tbody>
                {(quote.line_items || []).map((li, i) => (
                  <tr
                    key={`${li.description}-${i}`}
                    data-testid={`portal-quote-line-${i}`}
                    className="border-t border-[#E8EBED]"
                  >
                    <td className="text-left px-3 py-2 text-[#111315]">
                      {li.description || "—"}
                    </td>
                    <td className="text-right px-3 py-2 text-[#111315]">
                      {li.quantity || "—"}
                    </td>
                    <td className="text-right px-3 py-2 text-[#111315]">
                      ${li.rate || "0.00"}
                    </td>
                    <td className="text-right px-3 py-2 text-[#111315]">
                      ${li.amount || "0.00"}
                    </td>
                  </tr>
                ))}
                {(quote.line_items || []).length === 0 && (
                  <tr>
                    <td colSpan={4} className="px-3 py-3 text-center text-[#6B747C]">
                      No customer-facing line items.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
        <div className="text-[11px] text-[#6B747C] mt-4">
          Read-only. Contact your account manager to accept, decline, or request
          changes.
        </div>
      </div>
    </div>
  );
}

function UnauthorizedClientPanel() {
  return (
    <EmptyPanel
      testid="portal-unauthorized-client"
      title="Client unavailable"
      note="This client is not linked to your portal account, or is no longer accessible."
    />
  );
}

// -------- Shell wrapper ---------------------------------------------
function ShellInner() {
  const { loading, error, clients } = useW3b();
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [moreOpen, setMoreOpen] = useState(false);
  const nav = useNavigate();
  const location = useLocation();

  // Derive active client id from URL for the switcher.
  const match = location.pathname.match(/^\/portal\/clients\/([0-9a-f-]+)/i);
  const activeClientId = match ? match[1] : null;

  const onSwitch = useCallback(
    (nextId) => {
      // Any active client-scoped page resets to that client's tours list,
      // guaranteeing no stale state from the previous client renders.
      nav(`/portal/clients/${nextId}/tours`);
    },
    [nav]
  );

  if (loading) {
    return (
      <div className="min-h-screen grid place-items-center bg-[#F7F8F9]">
        <Loader2 size={22} className="animate-spin text-[#6B747C]" />
      </div>
    );
  }

  if (error === "session_expired") {
    return <Navigate to="/portal/login" replace />;
  }

  if (error === "load_failed") {
    return (
      <div className="min-h-screen grid place-items-center bg-[#F7F8F9] p-6">
        <div
          data-testid="portal-load-error"
          className="max-w-md w-full bg-white border border-[#E8EBED] rounded-sm p-6 text-center"
        >
          <div className="text-[14px] font-semibold text-[#111315]">
            Something went wrong loading the portal.
          </div>
          <div className="text-[12.5px] text-[#6B747C] mt-1">
            Please refresh, or contact your account manager if this persists.
          </div>
        </div>
      </div>
    );
  }

  if (!clients || clients.length === 0) {
    return (
      <div className="min-h-screen bg-[#F7F8F9]">
        <PortalTopNav activeClientId={null} onSwitch={onSwitch} />
        <MobileTopBar onOpenDrawer={() => setDrawerOpen(true)} activeClientId={null} onSwitch={onSwitch} />
        <main className="px-4 md:px-6 py-6 md:py-10" style={{ paddingBottom: "calc(env(safe-area-inset-bottom, 0px) + 5rem)" }}>
          <NoAuthorizedClientsView />
        </main>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#F7F8F9]">
      <PortalTopNav activeClientId={activeClientId} onSwitch={onSwitch} />
      <MobileTopBar
        onOpenDrawer={() => setDrawerOpen(true)}
        activeClientId={activeClientId}
        onSwitch={onSwitch}
      />
      <MobileDrawer
        open={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        activeClientId={activeClientId}
      />
      <MobileMoreSheet open={moreOpen} onClose={() => setMoreOpen(false)} />
      <main
        className="px-4 md:px-6 lg:px-8 py-4 md:py-6"
        style={{ paddingBottom: "calc(env(safe-area-inset-bottom, 0px) + 5rem)" }}
      >
        <Routes>
          <Route index element={<ClientPicker />} />
          <Route path=":cid/tours" element={<TourList />} />
          <Route path=":cid/tours/:tid" element={<TourDetail />} />
          <Route path=":cid/quotes" element={<QuoteList />} />
          <Route path=":cid/quotes/:qid" element={<QuoteDetail />} />
          <Route path="*" element={<Navigate to="/portal/clients" replace />} />
        </Routes>
      </main>
      <MobileBottomBar
        onOpenMore={() => setMoreOpen(true)}
        activeClientId={activeClientId}
      />
    </div>
  );
}

export default function W3bPortal() {
  const token = localStorage.getItem(AA_PORTAL_TOKEN);
  if (!token) return <Navigate to="/portal/login" replace />;
  return (
    <W3bProvider>
      <ShellInner />
    </W3bProvider>
  );
}
